
import React from "react";
import {
  FaCloud,
  FaHeart,
  FaLock,
  FaTools,
  FaRocket,
  FaCoffee,
} from "react-icons/fa";

/* ========================================================================== */
/*                            MONTANTS DE SOUTIEN                             */
/* ========================================================================== */

const SUPPORT_OPTIONS = [
  {
    amount: 2,
    label: "2 €",
    Icon: FaCoffee,
    url: "https://buy.stripe.com/4gM8wOfn5fDo7zufRU5AQ07",
  },
  {
    amount: 5,
    label: "5 €",
    Icon: FaHeart,
    url: "https://buy.stripe.com/cN2aFtaqL5fC1AkcMR",
  },
  {
    amount: 10,
    label: "10 €",
    Icon: FaRocket,
    url: "https://buy.stripe.com/dRm00ib6PdvgaLG6hk5AQ08",
  },
];

/* ========================================================================== */
/*                                DONATION CARD                               */
/* ========================================================================== */

export default function DonationCard({
  donationUrl,
  variant = "full",
}) {
  const compact = variant === "compact";

  return (
    <section
      aria-label="Soutenir le développement"
      className={`relative overflow-hidden rounded-[2rem] border border-pink-200 bg-white shadow-sm dark:border-pink-900/40 dark:bg-slate-900 ${
        compact ? "p-5" : "p-5 sm:p-6"
      }`}
    >
      {/* Décoration arrière-plan */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-36 w-36 rounded-full bg-pink-400/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-36 w-36 rounded-full bg-violet-400/20 blur-3xl" />

      <div className="relative">
        {/* Présentation */}
        <div className="flex items-start gap-3">
          <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-violet-500 text-white shadow-lg shadow-pink-500/20">
            <FaHeart className="text-xl" />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-pink-500">
              Soutien volontaire
            </p>

            <h3 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
              Aidez-moi à poursuivre le développement
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Ces applications demandent beaucoup de temps : maintenance,
              hébergement, corrections, évolutions et création de nouveaux
              outils pour les clubs. Chaque soutien, même symbolique,
              m'aide à continuer.
            </p>
          </div>
        </div>

        {/* Informations supplémentaires en version complète */}
        {!compact && (
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <DonationPoint
              icon={FaTools}
              title="Maintenance"
              text="Corrections, améliorations et nouvelles fonctionnalités."
            />

            <DonationPoint
              icon={FaRocket}
              title="Nouveaux projets"
              text="Création d'outils utiles aux clubs et aux enseignants."
            />

            <DonationPoint
              icon={FaCloud}
              title="Hébergement"
              text="Serveurs, stockage, sécurité et services techniques."
            />
          </div>
        )}

        {/* ============================================================ */}
        {/* BANDEAU DE SOUTIEN - 2 €, 5 €, 10 €                            */}
        {/* ============================================================ */}

        <div className="mt-5 rounded-[1.5rem] bg-gradient-to-r from-slate-950 via-violet-950 to-pink-900 p-4 text-white">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-bold">
                Un petit coup de pouce ?
              </p>

              <p className="mt-1 text-xs text-white/70">
                Choisissez librement votre soutien.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {SUPPORT_OPTIONS.map((option) => {
                const Icon = option.Icon;

                const url =
                  option.amount === 5 && donationUrl
                    ? donationUrl
                    : option.url;

                return (
                  <a
                    key={option.amount}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Soutenir avec ${option.label}`}
                    aria-label={`Soutien unique de ${option.label}`}
                    className="inline-flex min-h-11 min-w-[64px] items-center justify-center gap-1.5 rounded-xl bg-white px-3 text-sm font-extrabold text-slate-950 shadow-sm transition hover:-translate-y-0.5 hover:bg-pink-50 hover:shadow-md active:translate-y-0"
                  >
                    <Icon className="text-pink-500" />
                    {option.label}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sécurité */}
        <p className="mt-3 flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
          <FaLock />
          Soutien volontaire, sans obligation, sécurisé via Stripe.
        </p>
      </div>
    </section>
  );
}

/* ========================================================================== */
/*                        INFORMATIONS COMPLÉMENTAIRES                         */
/* ========================================================================== */

function DonationPoint({ icon: Icon, title, text }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white">
        <Icon />
      </div>

      <h4 className="mt-3 text-sm font-bold text-slate-950 dark:text-white">
        {title}
      </h4>

      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
        {text}
      </p>
    </div>
  );
}
